import { type ReactElement } from 'react';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { getTableV2LinkRoutingProps } from '@/features/shared/components/tableSystem/TableV2LinkRouting';
import { tableV2NestedDepthSx } from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2TagGroup,
  tableV2NestedBodyRowHeightPx,
  tableV2NamePrimaryFontSize,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { resolveEntityTags } from '@/features/shared/tags';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { s, typographyTokens } from '@/theme/tokens';
import { OverflowTooltip } from '@/ui/composites/table/cells/OverflowTooltip';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { Box, Link, TableCell, TableRow, Text, resolveStatusChipLabel } from '@/ui/primitives';
import { getSummaryName } from './ExecutionRunsTableV2NestedSorting';
import { StatusChip, formatUploadedAt, relativeTimeSx } from './ExecutionRunsTableV2RowShared';
import type { TagColor } from '@/shared/data/tags';
import type { ExecutionRunDto, TestRunDto, TestRunSummaryDto } from '@flowqa/shared';

const formatNestedTestRunNumber = (runNumber: number | null): string | null =>
  runNumber === null ? null : `#${String(runNumber)}`;

const resolveSummaryTimestamp = (
  timestamp: Date | null | undefined,
  parent: ExecutionRunDto,
): Date =>
  timestamp ?? parent.startedAt ?? parent.finishedAt ?? parent.resultsFileCreatedAt ?? new Date(0);

const buildTestRunFromSummary = ({
  parent,
  suiteRunId,
  suiteSlug,
  summary,
}: {
  parent: ExecutionRunDto;
  suiteRunId: string | null;
  suiteSlug: string | null;
  summary: TestRunSummaryDto;
}): TestRunDto | null => {
  if (!summary.executionId || !suiteRunId) return null;
  const timestamp = resolveSummaryTimestamp(summary.timestamp, parent);
  return {
    id: summary.executionId,
    runId: parent.id,
    resultsFileId: parent.resultsFileId ?? null,
    suiteRunId,
    testId: summary.testId,
    runNumber: summary.runNumber ?? null,
    testName: summary.testName ?? null,
    displayName: summary.displayName ?? null,
    groupPath: summary.groupPath,
    testSlug: summary.testSlug ?? null,
    tags: summary.tags ?? [],
    suiteName: summary.suiteName ?? null,
    suiteSlug,
    projectId: parent.projectId,
    projectName: parent.projectName ?? null,
    projectSlug: parent.projectSlug ?? null,
    organisationId: parent.organisationId,
    status: summary.status ?? 'untested',
    timestamp,
    durationMs: summary.durationMs ?? null,
    createdAt: parent.resultsFileCreatedAt ?? timestamp,
    deletedAt: null,
  };
};

export const TestRunDetailRow = ({
  deleteRowPendingId,
  editRowPendingId,
  parent,
  suiteRunId,
  suiteSlug,
  tagColorByName,
  test,
  onDelete,
  onEdit,
}: {
  deleteRowPendingId: string | null;
  editRowPendingId: string | null;
  parent: ExecutionRunDto;
  suiteRunId: string | null;
  suiteSlug: string | null;
  tagColorByName: Partial<Record<string, TagColor>>;
  test: TestRunSummaryDto;
  onDelete: (row: TestRunDto) => void;
  onEdit: (row: TestRunDto) => void;
}): ReactElement => {
  const testName = getSummaryName(test);
  const testRunNumber = test.runNumber ?? null;
  const testRunNumberLabel = formatNestedTestRunNumber(testRunNumber);
  const testRunHref =
    test.testSlug && testRunNumber !== null
      ? `/tests/${test.testSlug}/runs/${testRunNumber}`
      : null;
  const ranAt = test.timestamp ?? null;
  const ranAtLabel = formatRelativeTime(ranAt);
  const ranAtAbsoluteLabel = formatUploadedAt(ranAt);
  const durationLabel = formatDuration(test.durationMs ?? null);
  const tags = resolveEntityTags(test);
  const testRun = buildTestRunFromSummary({ parent, suiteRunId, suiteSlug, summary: test });

  return (
    <TableRow
      sx={[
        tableV2NestedDepthSx[2].bodyRow,
        {
          '& > td': {
            borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
            boxSizing: 'border-box',
            height: `${String(tableV2NestedBodyRowHeightPx)}px`,
            maxHeight: `${String(tableV2NestedBodyRowHeightPx)}px`,
            overflow: 'hidden',
            py: s('sm'),
            verticalAlign: 'middle',
          },
        },
      ]}
    >
      <TableCell>
        <Box
          sx={{
            alignContent: 'center',
            display: 'grid',
            gap: s('none'),
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          <Text
            role="body"
            component="div"
            sx={{
              fontSize: tableV2NamePrimaryFontSize,
              fontWeight: typographyTokens.body.fontWeight,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {testRunHref ? (
              <OverflowTooltip title={testName} inline>
                <Link
                  {...getTableV2LinkRoutingProps(testRunHref)}
                  sx={{
                    color: 'text.primary',
                    display: 'inline-block',
                    fontSize: tableV2NamePrimaryFontSize,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textDecorationColor: 'currentColor',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {testName}
                </Link>
              </OverflowTooltip>
            ) : (
              testName
            )}
          </Text>
          {testRunNumberLabel ? (
            <Text
              role="captionSmall"
              color="textSecondary"
              component="div"
              sx={{
                fontSize: (theme) => theme.typography.caption.fontSize,
                lineHeight: 1.2,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {testRunHref ? (
                <Link
                  {...getTableV2LinkRoutingProps(testRunHref)}
                  sx={{
                    color: 'text.secondary',
                    display: 'inline-block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textDecorationColor: 'currentColor',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {testRunNumberLabel}
                </Link>
              ) : (
                testRunNumberLabel
              )}
            </Text>
          ) : null}
        </Box>
      </TableCell>
      <TableCell>
        <StatusChip
          status={test.status ?? 'untested'}
          label={resolveStatusChipLabel(test.status ?? 'untested')}
        />
      </TableCell>
      <TableCell>
        <Text role="body" color="textSecondary">
          {durationLabel.length > 0 ? durationLabel : '-'}
        </Text>
      </TableCell>
      <TableCell>
        <Box sx={{ alignContent: 'center', display: 'grid', gap: s('none') }}>
          <Text
            role="body"
            color="textSecondary"
            component="div"
            sx={{
              lineHeight: 1.4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {ranAtAbsoluteLabel}
          </Text>
          <Text
            role="captionSmall"
            component="div"
            sx={[
              relativeTimeSx,
              {
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            ]}
          >
            {ranAtLabel || '-'}
          </Text>
        </Box>
      </TableCell>
      <TableCell>
        <TableV2TagGroup tagColorByName={tagColorByName} tags={tags} />
      </TableCell>
      <TableCell align="center">
        <TableActionsCell
          rowName={testName}
          verticalAlign="center"
          disabledTooltip={testRun ? null : 'Actions unavailable for this test run.'}
          editActionLabel={getEntityActionLabel('edit', 'Test Run')}
          deleteActionLabel={getEntityActionLabel('delete', 'Test Run')}
          onEdit={
            testRun
              ? () => {
                  onEdit(testRun);
                }
              : undefined
          }
          onDelete={
            testRun
              ? () => {
                  onDelete(testRun);
                }
              : undefined
          }
          disableEdit={
            testRun ? editRowPendingId === testRun.id || deleteRowPendingId === testRun.id : true
          }
          disableDelete={testRun ? deleteRowPendingId === testRun.id : true}
        />
      </TableCell>
    </TableRow>
  );
};
